import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { getLocale, useSelector, shallowEqual } from 'umi';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import TimePicker from '@/pages/dashboard/components/TimePicker';
import componentMap from '@/pages/dashboard/_config/compConfig';
import { FORM_MAP } from '@/pages/dashboard/pages/overview/_config/constant';
import { setBtnPointLog } from '@/pages/dashboard/utils/utils';
import { filterValueType } from '@/pages/dashboard/components/NewFilter/_type';
import { getDateDefault } from '@/pages/dashboard/utils/dateUtils';
import '@/pages/dashboard/components/Filter/index.scss';
import '@/pages/dashboard/pages/abnormalReport/abnormalReportFilter.scss';
import { resolveAnomalyReportTargetDate } from '@/pages/dashboard/pages/abnormalReport/utils';
import {
  mergeAbnormalReportServiceConfig,
  IFilterServiceConfig,
} from '@/pages/dashboard/pages/abnormalReport/defaultServiceConfig';
import { useAnomalyReportList } from '@/pages/dashboard/pages/abnormalReport/hooks/useAnomalyReportList';
import AbnormalReportDownloadBtn from '@/pages/dashboard/pages/abnormalReport/components/AbnormalReportDownloadBtn';
import type { TDownloadFormat } from '@/pages/dashboard/pages/abnormalReport/hooks/useReportDownload';
import type {
  IComponentFilterType,
  IComponentType,
  IConfigData,
} from '@/pages/dashboard/components/PageContainer/type';

type TInitialFilterState = 'loading' | 'error' | 'ready';

interface IButtonLogConfig {
  pageId: string;
  confirmButtonId: string;
  resetButtonId: string;
  typeId?: string;
}

/** 单个可下载格式配置 */
export interface IDownloadFormatConfig {
  /** 格式值 */
  format: TDownloadFormat;
  /** 按钮/Radio 文案（英文），不填则使用格式名大写，如 'HTML' */
  labelEn?: string;
  /** 按钮/Radio 文案（中文），不填则使用格式名大写，如 'HTML' */
  labelCn?: string;
}

interface IAbnormalReportFilterConfig
  extends Omit<IComponentFilterType, 'api' | 'dataKey' | 'beforeRequest' | 'formatData'>,
  Partial<IFilterServiceConfig> {
  /** 是否启用下载按钮插槽 */
  enableDownload?: boolean;
  /** 下载文件名（英文） */
  downloadFileNameEn?: string;
  /** 下载文件名（中文） */
  downloadFileNameCn?: string;
  /**
   * 可下载的格式列表，顺序即渲染顺序，第一项为默认选中格式。
   * - 配置 1 项：直接下载，不弹窗
   * - 配置多项：弹窗让用户选择
   * 不配置时默认等同于 [{ format: 'html' }, { format: 'pdf' }]
   */
  downloadFormats?: IDownloadFormatConfig[];
}

interface IAbnormalReportAllFilterProps {
  config?: IAbnormalReportFilterConfig;
  filterConfirm: (value: filterValueType) => void;
  filterInit: (value: filterValueType) => void;
  buttonLogConfig: IButtonLogConfig;
  children?: React.ReactNode;
  timeZone?: number;
  filterLoading?: boolean;
  data?: IConfigData;
}

interface IDashboardSelectorState {
  dashboard?: {
    currentGame?: {
      game_code?: string;
    };
  };
}

const buildFilterValue = (
  value: filterValueType,
  useLatestReport: boolean,
) => ({
  ...(value || {}),
  useLatestReport,
  [FORM_MAP.RANGE_TIME_DIFF]: useLatestReport,
  timeType: 'Day',
});

const AbnormalReportAllFilter: React.FC<IAbnormalReportAllFilterProps> = props => {
  const {
    config,
    filterConfirm,
    filterInit,
    buttonLogConfig,
    children,
    timeZone = 0,
    filterLoading = false,
    data,
  } = props;

  const locale = getLocale();
  const { gameCode } = useSelector(
    (state: IDashboardSelectorState) => ({
      gameCode: state?.dashboard?.currentGame?.game_code || '',
    }),
    shallowEqual,
  );

  const serviceConfig = useMemo(
    () => mergeAbnormalReportServiceConfig(config),
    [config],
  );

  const initializedRef = useRef(false);
  const [filterValue, setFilterValue] = useState<filterValueType | null>(null);
  const [initialFilterState, setInitialFilterState] =
    useState<TInitialFilterState>('loading');
  const { reportState, fetchReportList } = useAnomalyReportList(
    serviceConfig,
    data,
  );

  const defaultDate = useMemo(
    () =>
      getDateDefault({
        timeType: 'day',
        timeZone,
        singleNum: 3,
        isSingle: true,
      }) as string,
    [timeZone],
  );

  const componentListNode = useMemo(() => {
    return (config?.componentList || []).map((item: IComponentType, index: number) => {
      const itemComponent = componentMap[item?.name];
      if (!itemComponent) {
        return null;
      }
      return React.createElement(itemComponent, {
        key: `${item.name}-${index}`,
        reportState,
        initialFilterState,
        ...(item.props || {}),
      });
    });
  }, [config?.componentList, reportState, initialFilterState]);

  const singleDaily = filterValue?.singleDaily || defaultDate;

  const loadReport = useCallback(
    async (nextValue: filterValueType, syncLatestDate = false) => {
      const { list, errorMsg } = await fetchReportList(nextValue);

      if (syncLatestDate && list?.length) {
        const targetDate = resolveAnomalyReportTargetDate(list);
        if (targetDate && nextValue.singleDaily !== targetDate) {
          setFilterValue({
            ...nextValue,
            singleDaily: targetDate,
          });
        }
      }

      return { list, errorMsg };
    },
    [fetchReportList],
  );

  const handleInit = useCallback(
    async (value: filterValueType) => {
      const nextValue = buildFilterValue(value, true);
      setFilterValue(nextValue);
      setInitialFilterState('loading');

      try {
        const { errorMsg } = await loadReport(nextValue, true);
        setInitialFilterState(errorMsg ? 'error' : 'ready');
      } finally {
        filterInit(nextValue);
      }
    },
    [filterInit, loadReport],
  );

  const handleConfirm = useCallback(
    (value: filterValueType) => {
      const nextValue = buildFilterValue(value, false);
      setFilterValue(nextValue);
      filterConfirm(nextValue);
      loadReport(nextValue);
      setBtnPointLog(buttonLogConfig, value);
    },
    [filterConfirm, buttonLogConfig, loadReport],
  );

  const handleDateChange = useCallback(
    (date: moment.Moment) => {
      handleConfirm({
        ...(filterValue || {}),
        singleDaily: moment(date).format('YYYY-MM-DD'),
      });
    },
    [filterValue, handleConfirm],
  );

  useEffect(() => {
    if (initializedRef.current || !gameCode) {
      return;
    }
    initializedRef.current = true;
    handleInit({ singleDaily: defaultDate });
  }, [gameCode, defaultDate, handleInit]);

  const isInitialFilterLoading = initialFilterState === 'loading';
  const isInitialFilterError = initialFilterState === 'error';
  const enableDownload = config?.enableDownload ?? false;
  const downloadFileNameEn = config?.downloadFileNameEn;
  const downloadFileNameCn = config?.downloadFileNameCn;
  const downloadFormats = config?.downloadFormats;

  return (
    <div className="mod-filter dbd-abnormal-report-filter">
      <div className="mod-filter__inner dbd-filter-render-box">
        <Abnormal
          isLoading={false}
          componentLoading={isInitialFilterLoading}
          isError={isInitialFilterError}
          style={{ height: 36 }}
          advanceRender={false}
          component={
            <TimePicker
              matchButtonWidth
              className={classNames(
                'search-item',
                'dtb-timepicker-input-prefix',
                {
                  'dtb-language-cn': locale === 'zh-CN',
                },
              )}
              comKey="singleDaily"
              locale={locale}
              disabled={filterLoading}
              disabledDate={undefined}
              defaultValue={defaultDate}
              value={singleDaily}
              onConfirm={handleDateChange}
            />
          }
        />
        {enableDownload && (
          <AbnormalReportDownloadBtn
            reportState={reportState}
            initialFilterState={initialFilterState}
            downloadFileNameEn={downloadFileNameEn}
            downloadFileNameCn={downloadFileNameCn}
            downloadFormats={downloadFormats}
          />
        )}
      </div>
      <div className="dbd-abnormal-report-filter__content">
        {componentListNode || children}
      </div>
    </div>
  );
};

export default AbnormalReportAllFilter;
